import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

export const useAuthenticationStore = defineStore('authentication', () => {
  // ---------------------------------------------------
  // Pinia-States
  // ---------------------------------------------------
  const loaded = ref(false)
  const loggedIn = ref(false)
  const cn = ref('')
  const samAccount = ref('') // Neuer State für den SamAccount
  const roles = ref([])
  const highestRole = ref('')

  const highestRoleFormatted = computed(() => {
    if (highestRole.value === 'ROLE_ADMIN') {
      return 'Admin'
    } else if (highestRole.value === 'ROLE_TEACHER') {
      return 'Lehrer'
    } else if (highestRole.value === 'ROLE_STUDENT') {
      return 'Schüler'
    } else {
      return 'Alle'
    }
  })

  // ---------------------------------------------------
  // Lokale Storage-Funktionen
  // ---------------------------------------------------
  function loadLocalStorage() {
    try {
      const authData = JSON.parse(localStorage.getItem('authData'))
      if (authData && authData.loggedIn) {
        loggedIn.value = authData.loggedIn
        cn.value = authData.cn
        samAccount.value = authData.samAccount || '' // SamAccount aus dem LocalStorage laden
        roles.value = authData.roles
        highestRole.value = authData.highestRole
        loaded.value = true
      }
    } catch (e) {
      // Fehler ignorieren, falls der LocalStorage-Wert fehlerhaft oder leer ist
    }
  }

  function saveLocalStorage() {
    const authData = {
      loggedIn: loggedIn.value,
      cn: cn.value,
      samAccount: samAccount.value, // SamAccount speichern
      roles: roles.value,
      highestRole: highestRole.value
    }
    localStorage.setItem('authData', JSON.stringify(authData))
  }

  // ---------------------------------------------------
  // Haupt-Funktionen
  // ---------------------------------------------------
  async function checkAuth() {
    try {
      const response = await fetch(`${process.env.VUE_APP_BACKEND}/auth`, {
        method: 'GET',
        credentials: 'include'
      })
      if (!response.ok) {
        loaded.value = true
        loggedIn.value = false
        saveLocalStorage()
        return
      }
      const authData = await response.json()
      setAuthentication(authData)
    } catch (error) {
      loggedIn.value = false
      loaded.value = true
      saveLocalStorage()
    }
  }

  function setAuthentication(data) {
    const authorities = data.authorities
      ? data.authorities.map(a => a.authority)
      : []
    roles.value = authorities

    let role = 'alle'
    if (authorities.includes('ROLE_ADMIN')) {
      role = 'ROLE_ADMIN'
    } else if (authorities.includes('ROLE_TEACHER')) {
      role = 'ROLE_TEACHER'
    } else if (authorities.includes('ROLE_STUDENT')) {
      role = 'ROLE_STUDENT'
    }
    highestRole.value = role

    loggedIn.value = authorities.length > 0

    // cn nur setzen, wenn vorhanden
    if (data.cn) {
      cn.value = data.cn
    }
    // SamAccount setzen, wenn vorhanden
    if (data.samAccount) {
      samAccount.value = data.samAccount
    }

    loaded.value = true
    saveLocalStorage()
  }

  function setCN(newCn) {
    cn.value = newCn
    saveLocalStorage()
  }

  function logout() {
    cn.value = ''
    samAccount.value = '' // SamAccount zurücksetzen
    roles.value = []
    highestRole.value = ''
    loggedIn.value = false
    loaded.value = true
    saveLocalStorage()
  }

  function hasRole(role) {
    return roles.value.includes(role)
  }

  loadLocalStorage()

  return {
    loaded,
    loggedIn,
    cn,
    samAccount, // SamAccount nach außen verfügbar machen
    roles,
    highestRole,
    highestRoleFormatted,
    checkAuth,
    setAuthentication,
    setCN,
    logout,
    hasRole
  }
})
